import { css, cx } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Pagination, Button, Tooltip, Badge, PopoverBasicWithTrigger } from "@octopusdeploy/design-system-components";
import { ExperimentIcon } from "@octopusdeploy/design-system-icons";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { borderRadius, fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { Repository, PlatformHubVersionControlSettingsResource, ValidateGitRefV2Response, GitRef, GetProjectTemplatesBffResponse, ProjectTemplateSummary, ProjectTemplateUsage } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import type { ReactElement } from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { NewProjectTemplateWizard } from "~/areas/platformHub/projectTemplates/NewProjectTemplateWizard";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import { repository } from "~/clientInstance";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import InternalLink from "~/components/Navigation/InternalLink";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useOctopusTheme } from "~/components/Theme";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable";
import DateFormatter from "~/utils/DateFormatter/index";
interface LoaderData {
    projectTemplatesResponse: GetProjectTemplatesBffResponse;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
}
interface ProjectTemplatesPageProps {
    loaderData: LoaderData;
    queryParams: ProjectTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectTemplatesPageQueryParams>;
}
export interface ProjectTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyProjectTemplatesResponse: GetProjectTemplatesBffResponse = {
    ProjectTemplates: [],
    TotalResults: 0,
    ItemsPerPage: 0,
    TotalNoOfProjectTemplates: 0,
};
export async function projectTemplatesPageLoader(repository: Repository, queryParams: ProjectTemplatesPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            projectTemplatesResponse: emptyProjectTemplatesResponse,
            persistenceSettings,
            initialValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    const userPageSize = getValueInStorage(`Octopus.ProjectTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const projectTemplatesResponse = await repository.ProjectTemplates.getPaginatedTemplates({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: initialValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        projectTemplatesResponse,
        persistenceSettings,
        initialValidationResult,
    };
}
export function ProjectTemplatesPage({ loaderData, queryParams, setQueryParams }: ProjectTemplatesPageProps) {
    const [gitRef, setGitRef] = usePlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProjectTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyProjectTemplatesResponse);
        }
        return repository.ProjectTemplates.getPaginatedTemplates({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize], "Get paginated project templates", {
        initialResult: loaderData.projectTemplatesResponse,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const { execute: deleteProjectTemplate } = useMutation({
        name: "Delete Project Template",
        action: (repository: Repository, projectTemplate: ProjectTemplateSummary) => repository.ProjectTemplates.del({ Id: projectTemplate.Id, ChangeDescription: `Delete Project Template '${projectTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    if (!gitRef || !loaderData.persistenceSettings.Url) {
        return (<PageContent header={getProjectTemplatesHeader()}>
                <ConfigureVersionControlOnboarding />
            </PageContent>);
    }
    const addProjectTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Create Project Template",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <AddProjectTemplateButton gitRef={gitRef}/>,
    };
    const showOnboarding = pagingData.TotalNoOfProjectTemplates === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    if (showOnboarding) {
        return (<PageContent header={getProjectTemplatesHeader(titleAccessory)}>
                <AddProjectTemplatesOnboarding gitRef={gitRef}/>
            </PageContent>);
    }
    return (<PageContent header={getProjectTemplatesHeader(titleAccessory, [addProjectTemplateAction])} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Project Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <DataTable>
                <DataTableHeader>
                    <DataTableRow>
                        <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                        <DataTableHeaderColumn className={styles.versionColumn}>Version</DataTableHeaderColumn>
                        <DataTableHeaderColumn className={styles.lastPublishedColumn}>Last Published</DataTableHeaderColumn>
                        <DataTableHeaderColumn>Usage</DataTableHeaderColumn>
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {pagingData.ProjectTemplates.map((pt) => (<ProjectTemplateRow projectTemplate={pt} deleteProjectTemplate={deleteProjectTemplate} key={pt.Id}/>))}
                </DataTableBody>
            </DataTable>
        </PageContent>);
}
const getProjectTemplatesHeader = (contextSelector?: ReactElement, pageActions?: PageAction[]) => {
    const alphaBadge = (<div className={styles.badgeContainer}>
            <Tooltip content={"This feature is not finished or fully tested."}>
                <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/>
            </Tooltip>
        </div>);
    return { title: "Project Templates", contextSelector, chip: alphaBadge, pageActions };
};
//TODO put correct link to docs in callout
const AddProjectTemplatesOnboarding = ({ gitRef }: {
    gitRef: GitRef;
}) => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects." learnMore={null} actionButtons={<AddProjectTemplateButton gitRef={gitRef}/>}/>);
const ConfigureVersionControlOnboarding = () => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects. Project templates are stored in a Git repository. To add a project template, you'll first need to set up a Git connection in the Platform Hub." learnMore={null} actionButtons={<Button label={"Configure Version Control"} href={links.platformHubVersionControlSettingsPage.generateUrl()} importance="primary"/>}/>);
interface ProjectTemplateRowProps {
    projectTemplate: ProjectTemplateSummary;
    deleteProjectTemplate: (projectTemplate: ProjectTemplateSummary) => Promise<void>;
}
export function ProjectTemplateRow({ projectTemplate, deleteProjectTemplate }: ProjectTemplateRowProps) {
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <ProjectTemplateNameCell projectTemplate={projectTemplate}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={cx(styles.middle, styles.versionColumn)}>{projectTemplate.Version ? <div className={styles.rowCellPadding}>{projectTemplate.Version}</div> : null}</DataTableRowColumn>
            <DataTableRowColumn className={cx(styles.middle, styles.lastPublishedColumn)}>
                {projectTemplate.PublishedDate ? <div className={styles.rowCellPadding}>{DateFormatter.dateToLongFormat(projectTemplate.PublishedDate)}</div> : null}
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>{projectTemplate.Usages && <ProjectTemplateUsagesPopover projectTemplate={projectTemplate} usages={projectTemplate.Usages}/>}</DataTableRowColumn>
            <DataTableRowColumn className={styles.right}>
                <div className={styles.rowCellPadding}>
                    <ProjectTemplateOverflowMenu projectTemplate={projectTemplate} deleteProjectTemplate={deleteProjectTemplate}/>
                </div>
            </DataTableRowColumn>
        </DataTableRow>);
}
interface ProjectTemplateNameCellProps {
    projectTemplate: ProjectTemplateSummary;
}
function ProjectTemplateNameCell({ projectTemplate }: ProjectTemplateNameCellProps) {
    const linkTarget = links.projectTemplateDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef: projectTemplate.GitRef });
    return (<div className={styles.nameContainer}>
            <InternalLink className={styles.nameContainer} to={linkTarget}>
                <Avatar type="template" size="medium" src={repository.Icons.getIconUrl(projectTemplate.Icon)} alt="Project template icon"/>
                <div className={styles.name}>{projectTemplate.Name}</div>
            </InternalLink>
        </div>);
}
interface ProjectTemplateOverflowProps {
    projectTemplate: ProjectTemplateSummary;
    deleteProjectTemplate: (projectTemplate: ProjectTemplateSummary) => Promise<void>;
}
function ProjectTemplateOverflowMenu({ projectTemplate, deleteProjectTemplate }: ProjectTemplateOverflowProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    //overFlowActions.push(OverflowMenuItems.deleteItem("Delete", "Are you sure you want to delete this Project Template?", () => deleteProjectTemplate(projectTemplate), <DeleteProjectTemplate projectTemplateName={projectTemplate.Name} />));
    return <OverflowMenu menuItems={overFlowActions}/>;
}
function AddProjectTemplateButton({ gitRef }: {
    gitRef: GitRef;
}) {
    return (<OpenDialogButton disabled={false} label={"Create Project Template"} title={"Create new Project Template"} type={ActionButtonType.Primary} renderDialog={({ open, closeDialog }) => open && <NewProjectTemplateWizard gitRef={gitRef} closeDialog={closeDialog}/>}/>);
}
interface ProjectTemplateUsagesPopoverProps {
    projectTemplate: ProjectTemplateSummary;
    usages: ProjectTemplateUsage[];
}
const ProjectTemplateUsagesPopover = ({ projectTemplate, usages }: ProjectTemplateUsagesPopoverProps) => {
    const spaceUsageCount = new Set(usages?.map((u) => u.Space.Id)).size;
    const projectUsageCount = usages.length;
    const popoverStyles = {
        singleUsingSpaceContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[8],
        }),
        singleUsingSpaceText: css({
            fontSize: fontSize.medium,
            fontWeight: fontWeight[600],
            color: themeTokens.color.text.secondary,
            "&:hover": {
                color: themeTokens.color.text.primary,
            },
        }),
        multipleUsingSpacesText: css({
            fontSize: fontSize.small,
            color: themeTokens.color.text.link.default,
            "&:hover": {
                color: themeTokens.color.text.link.hover,
            },
        }),
        descriptionPrimaryText: css({
            fontWeight: fontWeight["600"],
            fontSize: fontSize.large,
        }),
        tableContainer: css({
            borderLeft: `1px solid ${useOctopusTheme().dividerLight}`,
            borderRight: `1px solid ${useOctopusTheme().dividerLight}`,
            borderTop: `1px solid ${useOctopusTheme().dividerLight}`,
            borderBottom: `1px solid ${useOctopusTheme().dividerLight}`,
            borderRadius: borderRadius.small,
            width: `732px`,
            marginTop: space[16],
            marginBottom: space[16],
            maxHeight: `415px`,
            overflowY: `auto`,
        }),
        spaceOrProjectTableCellContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[12],
        }),
        spaceOrProjectTableCellText: css({
            fontSize: fontSize.medium,
            fontWeight: fontWeight[400],
        }),
    };
    const TriggerElement = () => {
        if (spaceUsageCount === 1) {
            return (<div className={cx(popoverStyles.singleUsingSpaceContainer, styles.rowCellPadding)}>
                    <Avatar type="space" size="small" src={repository.Icons.getIconUrl(usages[0].Space.Icon)} alt="Icon of the space using the project template"/>
                    <div className={popoverStyles.singleUsingSpaceText}>{usages[0].Space.Name}</div>
                </div>);
        }
        return <div className={cx(popoverStyles.multipleUsingSpacesText, styles.rowCellPadding)}>{`${spaceUsageCount} ${spaceUsageCount === 1 ? "space" : "spaces"}`}</div>;
    };
    const Description = () => {
        return (<div>
                <div className={popoverStyles.descriptionPrimaryText}>{`${projectUsageCount} ${projectUsageCount === 1 ? "project" : "projects"} across ${spaceUsageCount} ${spaceUsageCount === 1 ? "space" : "spaces"} ${projectUsageCount === 1 ? "is" : "are"} using "${projectTemplate.Name}"`}</div>
                {usages.length > 0 && (<div className={popoverStyles.tableContainer}>
                        <DataTable>
                            <DataTableHeader>
                                <DataTableRow>
                                    <DataTableHeaderColumn>Space</DataTableHeaderColumn>
                                    <DataTableHeaderColumn>Project</DataTableHeaderColumn>
                                    <DataTableHeaderColumn>Current version</DataTableHeaderColumn>
                                </DataTableRow>
                            </DataTableHeader>
                            <DataTableBody>
                                {usages.map((usage) => {
                    return (<DataTableRow key={usage.Project.Id} displayNoBottomBorderForLastRow={true}>
                                            <DataTableRowColumn>
                                                <div className={popoverStyles.spaceOrProjectTableCellContainer}>
                                                    <Avatar type="space" size="medium" src={repository.Icons.getIconUrl(usage.Space.Icon)} alt="Icon of a space using the project template"/>
                                                    <div className={popoverStyles.spaceOrProjectTableCellText}>{usage.Space.Name}</div>
                                                </div>
                                            </DataTableRowColumn>
                                            <DataTableRowColumn>
                                                <div className={popoverStyles.spaceOrProjectTableCellContainer}>
                                                    <Avatar type="project" size="medium" src={repository.Icons.getIconUrl(usage.Project.Icon)} alt="Icon of a project using the project template"/>
                                                    <div className={popoverStyles.spaceOrProjectTableCellText}>{usage.Project.Name}</div>
                                                </div>
                                            </DataTableRowColumn>
                                            <DataTableRowColumn className={styles.middle}>{usage.Project.ProjectTemplateDetails && usage.Project.ProjectTemplateDetails.Version}</DataTableRowColumn>
                                        </DataTableRow>);
                })}
                            </DataTableBody>
                        </DataTable>
                    </div>)}
            </div>);
    };
    return <PopoverBasicWithTrigger description={<Description />} placement={"left-start"} width={"extra-wide"} triggerElement={<TriggerElement />}/>;
};
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", alignItems: "center", gap: space[12] }),
    name: css({ paddingLeft: space[6] }),
    badgeContainer: css({
        lineHeight: "0",
    }),
    versionColumn: css({
        width: "160px",
    }),
    lastPublishedColumn: css({
        width: "400px",
    }),
};
